import { useEffect } from 'react';

import { Auth0ProviderWithRouter } from '@/components/auth0-provider';
import { EnvBadge } from '@/components/env-badge';
import { ErrorMessage } from '@/components/error-message';
import { ThemeProvider } from '@/components/theme-provider';
import { Toaster } from '@/components/ui/toaster';
import { useSyncInternalUser } from '@/hooks/use-sync-internal-user';
import { useSentryUser } from '@/lib/hooks/use-sentry-user';
import { initMixpanel, trackEvent } from '@/lib/mixpanel';
import { loadCsvBox } from '@/lib/utils/load-csvbox';
import { loadHotjar } from '@/lib/utils/load-hotjar';
import * as Sentry from '@sentry/react';

import { AppRouter } from './router';

function useAnalyticsScripts() {
  useEffect(() => {
    loadHotjar();
    loadCsvBox();
    initMixpanel();
  }, []);
}

const fallback = (
  <ErrorMessage
    title="Une erreur est survenue"
    message="L'application a rencontré une erreur inattendue. Merci de recharger la page."
    actions={[{ label: 'Recharger', onClick: () => window.location.reload() }]}
  />
);

function AuthenticatedApp() {
  useSentryUser();
  useSyncInternalUser();

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="system"
      enableSystem
      disableTransitionOnChange
    >
      <div className="min-h-screen font-sans">
        <AppRouter />
      </div>
      <Toaster />
      <EnvBadge />
    </ThemeProvider>
  );
}

export default function App() {
  useAnalyticsScripts();

  return (
    <Sentry.ErrorBoundary
      fallback={fallback}
      onError={(error) => {
        trackEvent('APP_ERROR_BOUNDARY_CAUGHT', {
          errorMessage: error instanceof Error ? error.message : String(error),
        });
      }}
    >
      <Auth0ProviderWithRouter>
        <AuthenticatedApp />
      </Auth0ProviderWithRouter>
    </Sentry.ErrorBoundary>
  );
}
